import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeeplinkByCode } from '@/services/deeplinks';
import type { Deeplink, Platform } from '@/types';

// Android package names for Intent URLs
const androidPackages: Partial<Record<Platform, string>> = {
  spotify: 'com.spotify.music',
  youtube: 'com.google.android.youtube',
  tiktok: 'com.zhiliaoapp.musically',
  instagram: 'com.instagram.android',
  facebook: 'com.facebook.katana',
  twitter: 'com.twitter.android',
  whatsapp: 'com.whatsapp',
  amazon: 'com.amazon.mShop.android.shopping',
  'amazon-music': 'com.amazon.mp3',
  'apple-music': 'com.apple.android.music',
  linkedin: 'com.linkedin.android',
  deezer: 'deezer.android.app',
  soundcloud: 'com.soundcloud.android',
  'google-maps': 'com.google.android.apps.maps',
  notion: 'notion.id',
  slack: 'com.Slack',
  booking: 'com.booking',
  ebay: 'com.ebay.mobile',
  etsy: 'com.etsy.android',
  vinted: 'fr.vinted',
  walmart: 'com.walmart.android',
};

// iOS App Store IDs for App Store fallback
const iosAppStoreIds: Partial<Record<Platform, string>> = {
  spotify: '324684580',
  'apple-music': '1108187390',
  deezer: '292738169',
  soundcloud: '336353151',
  'amazon-music': '510855668',
  'apple-podcasts': '525463029',
  youtube: '544007664',
  'youtube-music': '1017492454',
  tiktok: '835599320',
  instagram: '389801252',
  facebook: '284882215',
  linkedin: '288429040',
  twitter: '333903271',
  whatsapp: '310633997',
  amazon: '297606951',
  etsy: '477128284',
  ebay: '282614216',
  walmart: '338137227',
  vinted: '632064380',
  booking: '367003839',
  'google-maps': '585027354',
  notion: '1232780281',
  slack: '618783545',
  snapchat: '447188370',
  pinterest: '429047995',
  reddit: '1064216828',
  telegram: '686449807',
  messenger: '454638411',
  twitch: '460177396',
  tidal: '913943275',
  shazam: '284993459',
};

const isAndroid = (): boolean => /android/i.test(navigator.userAgent);
const isIos = (): boolean => /iphone|ipad|ipod/i.test(navigator.userAgent);

const getAppStoreUrl = (platform: Platform): string | undefined => {
  const id = iosAppStoreIds[platform];
  return id ? `https://apps.apple.com/app/id${id}` : undefined;
};

// Convert URI scheme deeplink to Android Intent URL
const toIntentUrl = (deepLinkUrl: string, fallbackUrl: string, platform: Platform): string => {
  const pkg = androidPackages[platform];
  if (!pkg) return deepLinkUrl;

  // Parse scheme and path from deepLinkUrl (e.g. "spotify://track/xxx")
  const match = deepLinkUrl.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/(.*)$/);
  if (!match) return deepLinkUrl;

  const [, scheme, path] = match;
  return `intent://${path}#Intent;scheme=${scheme};package=${pkg};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
};

interface DeeplinkRedirectProps {
  code: string;
}

const DeeplinkRedirect = ({ code }: DeeplinkRedirectProps) => {
  const { t } = useTranslation();
  const [deeplink, setDeeplink] = useState<Deeplink | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const data = await getDeeplinkByCode(code);
        if (!data) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setDeeplink(data);
        setLoading(false);

        // Fire-and-forget tracking
        const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
        fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/track`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              deeplinkId: data.id,
              userId: data.userId,
            }),
          }
        ).catch(() => {});

        // Try native app deeplink
        if (data.deepLinkUrl && data.deepLinkUrl !== data.fallbackUrl) {
          // On Android, try Intent URL first (works with user gesture, may be blocked programmatically)
          const redirectUrl = isAndroid()
            ? toIntentUrl(data.deepLinkUrl, data.fallbackUrl, data.platform)
            : data.deepLinkUrl;
          window.location.href = redirectUrl;
        }

        // Fallback after delay: iOS → App Store, others → web URL
        const fallbackTarget = isIos()
          ? (getAppStoreUrl(data.platform) || data.fallbackUrl)
          : data.fallbackUrl;
        if (fallbackTarget) {
          setTimeout(() => {
            window.location.href = fallbackTarget;
          }, 1500);
        }
      } catch (err) {
        console.error('Failed to load deeplink:', err);
        setNotFound(true);
        setLoading(false);
      }
    };

    fetchAndRedirect();
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-200 dark:border-dark-700 border-t-lime-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('deeplinkRedirect.redirecting')}</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">
            {t('deeplinkRedirect.linkNotFound')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('deeplinkRedirect.linkNotFoundMessage')}
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center px-6 py-3 bg-lime-400 text-dark-800 font-medium rounded-full hover:bg-lime-500 transition-colors"
          >
            {t('deeplinkRedirect.goToKlipr')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-gray-200 dark:border-dark-700 border-t-lime-400 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{t('deeplinkRedirect.redirecting')}</p>
        {deeplink && (
          <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">
            {t('deeplinkRedirect.notWorking')}{' '}
            <a
              href={
                isAndroid() && deeplink.deepLinkUrl !== deeplink.fallbackUrl
                  ? toIntentUrl(deeplink.deepLinkUrl, deeplink.fallbackUrl, deeplink.platform)
                  : deeplink.fallbackUrl
              }
              className="text-lime-500 hover:text-lime-600"
            >
              {t('deeplinkRedirect.openTheApp')}
            </a>
            {' · '}
            <a href={deeplink.fallbackUrl} className="text-lime-500 hover:text-lime-600">
              {t('deeplinkRedirect.openInBrowser')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default DeeplinkRedirect;
